import { useCallback, useEffect } from 'react';

import {
  FileBlockIcon,
  Files01Icon,
  InformationCircleIcon,
  Upload01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { FilesFolders } from 'constants/files-folders-enum';
import { DisableCandidateFormActionsProvider } from 'contexts/contexts';
import { useApiUploadActions } from 'hooks/upload-actions';
import { useWatch } from 'react-hook-form';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { InputGroup, MultipleDocumentUpload, Toggle } from 'components/FormComponents';

export const Documents = ({
  register,
  readOnly = false,
  backgroundColor = 'background-secondary',
  brand,
  control,
  getBrandUploads = () => {},
  removeBrandUpload = () => {},
  addBrandUpload = () => {},
  setLoading = () => {},
}) => {
  const { signUpload, createUpload, deleteUpload, getUpload } = useApiUploadActions();

  const documentsActiveValue = useWatch({ control, name: 'documents_active' });

  useEffect(() => {
    setLoading(true);
    const result = getBrandUploads(brand?.id);
    Promise.resolve(result).then(() => setLoading(false));
  }, [brand?.id]);

  const syncDocumentsToRedux = useCallback(
    newList => {
      if (!brand?.id) return;
      const prev = brand.uploads || [];
      const prevIds = new Set(prev.map(u => u.id));
      const nextWithIds = (newList || []).filter(u => u.id);
      const nextIds = new Set(nextWithIds.map(u => u.id));
      for (const u of prev) {
        if (!nextIds.has(u.id)) {
          removeBrandUpload(brand.id, u.id);
        }
      }
      for (const u of nextWithIds) {
        if (!prevIds.has(u.id)) {
          addBrandUpload(brand.id, u);
        }
      }
    },
    [brand?.id, brand?.uploads, addBrandUpload, removeBrandUpload],
  );

  return (
    <BrandingTabSplitLayout key={brand?.id} shellClassName={backgroundColor}>
      <div className="flex flex-col gap-8">
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex justify-between items-start">
          <div className="flex gap-2 flex-grow-1 w-3/4">
            <HugeiconsIcon icon={Files01Icon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2">
              <span className="text-2xl font-medium text-neutral-7">Documents</span>
              <p className="text-xl text-text-tertiary">Upload documents to your brand. </p>
            </div>
          </div>
          <div className="w-auto flex-shrink-0">
            <InputGroup noMargin>
              <Toggle register={register} name="documents_active" small />
            </InputGroup>
          </div>
        </section>
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex flex-col gap-8">
          <div className="flex w-full gap-2">
            <HugeiconsIcon icon={Upload01Icon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2">
              <span className="text-2xl font-medium text-neutral-7">Upload Documents</span>
              <p className="text-xl text-text-tertiary">
                Upload documents in PDF, DOC or DOCX format (max 10MB each).
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col items-center justify-center gap-4 border-t border-neutral-4 pt-8">
            {documentsActiveValue ? (
              <InputGroup noMargin>
                <DisableCandidateFormActionsProvider>
                  <MultipleDocumentUpload
                    documentTitle="Upload files"
                    value={brand?.uploads ?? []}
                    onChange={syncDocumentsToRedux}
                    modelParams={{ brand_id: brand?.id }}
                    bucketFolder={FilesFolders.CompanyDocuments}
                    accept={{
                      'application/pdf': ['.pdf'],
                      'application/msword': ['.doc', '.docx'],
                    }}
                    maxSize={10485760}
                    signUpload={signUpload}
                    createUpload={createUpload}
                    deleteUpload={deleteUpload}
                    getUpload={getUpload}
                    useModel
                    brand={brand}
                    disabled={readOnly}
                    fullWidth
                  />
                </DisableCandidateFormActionsProvider>
              </InputGroup>
            ) : (
              <div className="flex flex-col gap-8 w-full items-center justify-center">
                <HugeiconsIcon
                  icon={FileBlockIcon}
                  size="80"
                  strokeWidth="1.5"
                  className="text-neutral-6"
                />
                <div className="flex w-full items-center justify-center gap-2">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    size="20"
                    strokeWidth="1.5"
                    className="text-neutral-6"
                  />
                  <p className="text-xl text-text-tertiary">
                    Upload not available. Documents are turned off. Enable the toggle above to add
                    files.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </BrandingTabSplitLayout>
  );
};
